import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import * as cciApi from '@di-shared/api/cci/auth';
import {diApiEu} from '@di-shared/api/di';
import LOGO_KOOPERATIVA from '@di-shared/assets/img/home/logo_kooperatlva.png';
import LOGO_MAPFRE from '@di-shared/assets/img/home/logo_mapfre.png';
import {ERROR} from '@di-shared/const';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useAvailableComponentEu from '@di-shared/features/home/eu/useAvailableComponent.eu';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {WebviewUtils} from '@shared/webviewUtils';
import {useQueryClient, useSuspenseQuery} from '@tanstack/react-query';

export interface BenefitContentsEuProps {
  vin: string;
}

const UBI_INSURER_LOGO: Record<string, string> = {
  MAPFRE: LOGO_MAPFRE,
  KOOPERATIVA: LOGO_KOOPERATIVA,
};

// 국가별 UBI 보험사 1개 고정 (KCE 협의) - 미동의 상태는 /ubi/insurers 응답이 없어 국가로 대체
const COUNTRY_INSURER_LOGO: Record<string, string> = {
  CZ: LOGO_KOOPERATIVA,
  ES: LOGO_MAPFRE,
};

function BenefitContentsEu({vin}: BenefitContentsEuProps) {
  const {t} = useInferableTranslation();

  return (
    <section
      id="tabpanel03"
      className="section"
      aria-labelledby="tab03"
    >
      <section className="section section_ubi">
        <ErrorBoundary
          fallback={
            <div className="section_container">
              <div className="section_container_header">
                <div className="container_header_title">
                  <h3 className="label_medium_b">{t('ctn_ubi_component_title_eu')}</h3>
                  <strong className="title_xxsmall">{ERROR.DASH}</strong>
                </div>
                <div className="container_header_imgbox">
                  <ImageSelector
                    className="driving_banner_img"
                    alt=""
                    kiaLightSrc={() => import('@di-shared/assets/img/home/ubi_discount_light.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/home/ubi_discount.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount_light.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount_light.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount.png')}
                  />
                </div>
              </div>
              <p className="section_container_desc">{t('msg_ubi_component_suggest_message_$company_eu', {Companyname: ERROR.DASH})}</p>
            </div>
          }
        >
          <Suspense
            fallback={
              <div className="section_container">
                <div className="section_container_header">
                  <div className="container_header_title">
                    <h3 className="label_medium_b skeleton">{t('ctn_ubi_component_title_eu')}</h3>
                    <strong className="title_xxsmall skeleton">{t('ctn_ubi_component_title_eu')}</strong>
                  </div>
                  <div className="container_header_imgbox">
                    <ImageSelector
                      className="driving_banner_img"
                      alt=""
                      kiaLightSrc={() => import('@di-shared/assets/img/home/ubi_discount_light.png')}
                      kiaDarkSrc={() => import('@di-shared/assets/img/home/ubi_discount.png')}
                      hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount_light.png')}
                      hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount.png')}
                      genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount_light.png')}
                      genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount.png')}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn_large btn_full skeleton"
                >
                  {t('ctn_ubi_component_discover_potential_button_eu')}
                </button>
              </div>
            }
          >
            <Inner vin={vin} />
          </Suspense>
        </ErrorBoundary>
      </section>
    </section>
  );
}

function Inner({vin}: BenefitContentsEuProps) {
  const {isUbiAgreed} = useAvailableComponentEu({vin});

  // UBI 동의 상태
  if (isUbiAgreed) {
    return <AgreedContents vin={vin} />;
  }

  // UBI 미동의 상태
  return <UnagreedContents vin={vin} />;
}

function UnagreedContents({vin}: BenefitContentsEuProps) {
  const {t} = useInferableTranslation();
  const queryClient = useQueryClient();
  const {resolveError} = useApiError();
  const {data: ubiLogo} = useSuspenseQuery({
    queryKey: [getQueryKey(cciApi.getMe)],
    queryFn: () => cciApi.getMe(),
    select: (data) => COUNTRY_INSURER_LOGO[data.address.country?.toUpperCase()],
  });

  async function openAgree() {
    const confirmed = await ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_UBI_DETAIL_$SERVICE_ID_AGREE.getFullPath(), {
      vin,
    });
    if (!confirmed) {
      return;
    }
    try {
      await diApiEu.vehicle.consentSelectiveTermsCode({vin, termscode: 'UBI', agree: true});
      await queryClient.invalidateQueries({queryKey: [getQueryKey(diApiEu.vehicle.getVehicleInfo), vin]});
    } catch (error) {
      await resolveError(error);
    }
  }

  return (
    <div className="section_container">
      <div className="section_container_header">
        <div className="container_header_title">
          {ubiLogo && (
            <span className="ubi_logo">
              <img
                src={ubiLogo}
                alt=""
                style={{width: 'auto'}}
              />
            </span>
          )}
          <h3 className="label_medium_b">{t('ctn_ubi_component_title_eu')}</h3>
          <strong className="title_xxsmall">{t('ctn_ubi_component_consent_title_eu')}</strong>
        </div>
        <div className="container_header_imgbox">
          <ImageSelector
            className="driving_banner_img"
            alt=""
            kiaLightSrc={() => import('@di-shared/assets/img/home/ubi_discount_light.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/home/ubi_discount.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount_light.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount_light.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount.png')}
          />
        </div>
      </div>

      <p className="section_container_desc">{t('ctn_ubi_component_consent_message_eu')}</p>
      <button
        type="button"
        className="btn btn_large btn_filled btn_full"
        onClick={openAgree}
      >
        {t('ctn_ubi_consent_discover_button_eu')}
      </button>
    </div>
  );
}

function AgreedContents({vin}: BenefitContentsEuProps) {
  const {t} = useInferableTranslation();
  const queryClient = useQueryClient();
  const {resolveError} = useApiError();
  const {openConfirm} = useCcWebviewUtilsHelper();
  const {data: insurers} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.ubi.getInsurers), vin],
    queryFn: () => diApiEu.ubi.getInsurers({vin}),
    select: ({data}) => data,
  });

  const insurer = insurers[0];
  const offer = insurers.find((insurer) => insurer.url);
  const ubiLogo = insurer && UBI_INSURER_LOGO[insurer.code.toUpperCase()];

  async function openInsurerOffer() {
    if (!offer?.url) {
      return;
    }
    const confirmed = await openConfirm({
      title: t('msg_ubi_external_title_eu'),
      message: t('msg_ubi_external_content_eu'),
      okText: t('ctn_ubi_external_continue_eu'),
      cancelText: t('ctn_ubi_external_cancel_eu'),
    });
    if (confirmed) {
      WebviewUtils.openWebBrowser(offer.url);
    }
  }

  async function openWithdraw() {
    const confirmed = await ccWebviewUtils.openBottomSheetWebViewUrl(
      ROUTE_PATHS.BOTTOM_SHEET_UBI_DETAIL_$SERVICE_ID_WITHDRAW.getFullPath(),
      {vin}
    );
    if (!confirmed) {
      return;
    }
    try {
      await diApiEu.vehicle.consentSelectiveTermsCode({vin, termscode: 'UBI', agree: false});
      await queryClient.invalidateQueries({queryKey: [getQueryKey(diApiEu.vehicle.getVehicleInfo), vin]});
    } catch (error) {
      await resolveError(error);
    }
  }

  // 점수 구간 조건 충족 (보험사 offer 존재)
  if (offer) {
    return (
      <>
        <div className="section_container">
          <div className="section_container_header">
            <div className="container_header_title">
              {ubiLogo && (
                <span className="ubi_logo">
                  <img
                    src={ubiLogo}
                    alt={offer.name}
                    style={{width: 'auto'}}
                  />
                </span>
              )}
              <h3 className="label_medium_b">{t('ctn_ubi_component_title_eu')}</h3>
              <strong className="title_xxsmall">{t('ctn_ubi_component_suggest_title_eu')}</strong>
            </div>
            <div className="container_header_imgbox">
              <ImageSelector
                className="driving_banner_img"
                alt=""
                kiaLightSrc={() => import('@di-shared/assets/img/home/ubi_discount_light.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/home/ubi_discount.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount_light.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount_light.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount.png')}
              />
            </div>
          </div>
          <p className="section_container_desc">{t('msg_ubi_component_suggest_message_$company_eu', {Companyname: offer.name})}</p>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            onClick={openInsurerOffer}
          >
            {t('ctn_ubi_component_discover_potential_button_eu')}
          </button>
        </div>
        <button
          type="button"
          className="consent_withdraw_btn"
          onClick={openWithdraw}
        >
          {t('msg_ubi_withdraw_enter_eu')}
        </button>
      </>
    );
  }

  // 점수 구간 조건 미충족
  return (
    <>
      <div className="section_container">
        <div className="section_container_header">
          <div className="container_header_title">
            {ubiLogo && (
              <span className="ubi_logo">
                <img
                  src={ubiLogo}
                  alt=""
                  style={{width: 'auto'}}
                />
              </span>
            )}
            <h3 className="label_medium_b">{t('ctn_ubi_component_title_eu')}</h3>
            <strong className="title_xxsmall">{t('msg_ubi_component_not_available_title_eu')}</strong>
          </div>
          <div className="container_header_imgbox">
            <ImageSelector
              className="driving_banner_img"
              alt=""
              kiaLightSrc={() => import('@di-shared/assets/img/home/ubi_discount_light.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/home/ubi_discount.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount_light.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/ubi_discount.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount_light.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/ubi_discount.png')}
            />
          </div>
        </div>
        <p className="section_container_desc">{t('msg_ubi_component_not_available_content_eu')}</p>
      </div>
      <button
        type="button"
        className="consent_withdraw_btn"
        onClick={openWithdraw}
      >
        {t('msg_ubi_withdraw_enter_eu')}
      </button>
    </>
  );
}

export default BenefitContentsEu;
